import React, { useEffect, useMemo, useState } from 'react';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { ClipboardList, Loader2 } from 'lucide-react';
import { BaseLayout } from '@/shared/components/layout';
import { useDailyLogForDate } from '../hooks';
import { ManpowerSection } from './ManpowerSection';
import { NotesSection } from './NotesSection';
import { DateNavigator } from './DateNavigator';
import { DAILY_LOG_TOUR_ID } from '@/features/tours/daily-log';
import { useTour } from '@/features/tours';

const todayIso = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const DailyLogPage: React.FC = () => {
  const { projectIdNumber: numericProjectId } = useCurrentProject();

  const [date, setDate] = useState<string>(todayIso());
  const [manpowerExpanded, setManpowerExpanded] = useState(true);
  const [notesExpanded, setNotesExpanded] = useState(true);

  const { data: dailyLog, isLoading, isError } = useDailyLogForDate(
    numericProjectId,
    date,
  );

  const headerStats = useMemo(() => {
    if (!dailyLog) return null;
    return {
      workers: dailyLog.total_workers,
      hours: dailyLog.total_hours,
    };
  }, [dailyLog]);

  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return;
    if (!numericProjectId) return;
    if (!dailyLog) return;
    if (hasSeen(DAILY_LOG_TOUR_ID)) return;
    void startTour(DAILY_LOG_TOUR_ID);
  }, [tourHydrated, active, startTour, hasSeen, numericProjectId, dailyLog]);

  return (
    <BaseLayout>
      <div className="mx-auto w-full min-w-0 max-w-[100rem] space-y-4 p-4 sm:p-6">
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <ClipboardList className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold">Daily Log</h1>
            {headerStats && (
              <span className="ml-3 hidden text-sm text-muted-foreground md:inline">
                {headerStats.workers} worker{headerStats.workers === 1 ? '' : 's'}
                {' · '}
                {headerStats.hours} hrs
              </span>
            )}
          </div>
          <div className="max-w-full" data-tour-id="daily-log-date-nav">
            <DateNavigator date={date} onChange={setDate} />
          </div>
        </div>

        {!numericProjectId && (
          <div className="rounded-md border bg-card p-6 text-center text-sm text-muted-foreground">
            Select a project from the sidebar to view its daily log.
          </div>
        )}

        {numericProjectId && isLoading && (
          <div className="flex items-center gap-2 rounded-md border bg-card p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading daily log…
          </div>
        )}

        {numericProjectId && isError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-6 text-sm text-destructive">
            Failed to load the daily log. Please try refreshing.
          </div>
        )}

        {numericProjectId && dailyLog && (
          <div className="space-y-4">
            <div data-tour-id="daily-log-manpower">
              <ManpowerSection
                projectId={numericProjectId}
                dailyLog={dailyLog}
                expanded={manpowerExpanded}
                onToggle={() => setManpowerExpanded((prev) => !prev)}
              />
            </div>
            <div data-tour-id="daily-log-notes">
              <NotesSection
                projectId={numericProjectId}
                dailyLog={dailyLog}
                expanded={notesExpanded}
                onToggle={() => setNotesExpanded((prev) => !prev)}
              />
            </div>
          </div>
        )}
      </div>
    </BaseLayout>
  );
};
